import { useEffect, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import Button from "components/Button";
import ConfigureIMAP from "components/ConfigureIMAP";
import settingService from "api/settingsService";
import { IntegrationProvider } from "dto/Settings/IntegrationProvider";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { ENotificationType, notification } from "utils/notification";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";

type Method = "google" | "microsoft" | "imap";

interface Props {
  email: string;
  onClose: () => void;
  /** Wywoływane po pomyślnym dodaniu integracji skrzynki. */
  onConnected: () => void;
}

const GOOGLE_DOMAINS = ["gmail.com", "googlemail.com"];
const MICROSOFT_DOMAINS = [
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "hotmail.co.uk",
  "outlook.pl",
];

// Zakresy OAuth dla integracji poczty (zgodne z IntegrationsTab).
const GOOGLE_EMAIL_SCOPES = [
  "https://mail.google.com/",
  "https://www.googleapis.com/auth/contacts.readonly",
];
const MICROSOFT_EMAIL_SCOPES = [
  "user.read",
  "mail.readwrite",
  "mail.send",
  "contacts.readwrite",
];

// Dobór metody integracji do operatora skrzynki na podstawie domeny adresu.
// Gdy operator nierozpoznany — przyjmujemy IMAP/POP3.
const resolveMethod = (email: string): Method => {
  const domain = email.split("@")[1]?.toLowerCase();
  if (domain && GOOGLE_DOMAINS.includes(domain)) return "google";
  if (
    domain &&
    (MICROSOFT_DOMAINS.includes(domain) || domain.startsWith("outlook."))
  )
    return "microsoft";
  return "imap";
};

const METHOD_LABEL: Record<Exclude<Method, "imap">, string> = {
  google: "Konto Google (OAuth)",
  microsoft: "Konto Microsoft (OAuth)",
};

const EmailIntegrationWizard = ({ email, onClose, onConnected }: Props) => {
  const { t } = useTranslation();
  const method = resolveMethod(email);

  const { mutate: configureIntegration, isLoading } =
    settingService.useConfigureIntegration();
  const { mutate: getMsRedirect } = settingService.useGetMsredirect();

  // Okno popup logowania Microsoft. Wynik wraca przez postMessage z main.tsx
  // (callback popupu OAuth na naszym origin).
  const msPopupRef = useRef<Window | null>(null);

  const closeMsPopup = () => {
    if (msPopupRef.current && !msPopupRef.current.closed)
      msPopupRef.current.close();
    msPopupRef.current = null;
  };

  // Google — logowanie OAuth (auth-code) i konfiguracja integracji poczty.
  const loginGoogle = useGoogleLogin({
    scope: GOOGLE_EMAIL_SCOPES.join(" "),
    flow: "auth-code",
    onSuccess: (codeResponse) => {
      const missing = GOOGLE_EMAIL_SCOPES.some(
        (s) => codeResponse.scope.indexOf(s) < 0,
      );
      if (missing) {
        notification({
          title: t("integrations.insufficientPermissions"),
          text: t("integrations.integrationSetupFailed"),
          type: ENotificationType.ERROR,
        });
        return;
      }
      configureIntegration(
        {
          Provider: IntegrationProvider.Google,
          Type: IntegrationType.Email,
          Code: codeResponse.code,
          Scope: codeResponse.scope,
        },
        { onSuccess: () => onConnected() },
      );
    },
  });

  const finalizeMicrosoft = (code: string) => {
    configureIntegration(
      {
        Provider: IntegrationProvider.Microsoft,
        Type: IntegrationType.Email,
        Code: code,
        Scope: MICROSOFT_EMAIL_SCOPES.join(", "),
      },
      {
        onSuccess: () => {
          notification({
            title: t("integrations.title"),
            text: "Integracja skrzynki Microsoft została dodana.",
            type: ENotificationType.SUCCESS,
          });
          onConnected();
        },
        onError: () => {
          notification({
            title: t("integrations.createIntegrationFailed"),
            text: t("errors.somethingWentWrong"),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  // Wynik logowania Microsoft wraca z popupu przez postMessage (main.tsx).
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data;
      if (!d || d.type !== "oauth-callback") return;
      closeMsPopup();
      if (d.code) {
        finalizeMicrosoft(d.code);
      } else if (d.error) {
        notification({
          title: t("integrations.createIntegrationFailed"),
          text: d.errorDescription || d.error,
          type: ENotificationType.ERROR,
        });
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      closeMsPopup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectMicrosoft = () => {
    // Okno otwieramy SYNCHRONICZNIE w geście użytkownika — w przeciwnym razie
    // (po asynchronicznym pobraniu URL) blokuje je popup blocker przeglądarki.
    const width = 480;
    const height = 640;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    const popup = window.open(
      "",
      "ms-oauth",
      `width=${width},height=${height},left=${left},top=${top}`,
    );

    if (!popup) {
      notification({
        title: t("integrations.integrationSetupFailed"),
        text: "Zezwól na wyskakujące okna, aby zalogować się do Microsoft.",
        type: ENotificationType.ERROR,
      });
      return;
    }

    msPopupRef.current = popup;

    getMsRedirect(
      { scopes: MICROSOFT_EMAIL_SCOPES.join(", ") },
      {
        onSuccess: (data) => {
          if (msPopupRef.current && !msPopupRef.current.closed) {
            msPopupRef.current.location.href = data.data.Url;
          }
        },
        onError: () => {
          if (msPopupRef.current && !msPopupRef.current.closed)
            msPopupRef.current.close();
        },
      },
    );
  };

  // IMAP/POP3 — od razu konfigurator (sekcja „Konfiguracja skrzynki"),
  // pole Etykieta i Hasło puste.
  if (method === "imap") {
    return (
      <div className={styles.wrapper}>
        <ConfigureIMAP
          initialEmail={email}
          initialLabel=""
          onSuccess={() => onConnected()}
          toggle={onClose}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.email}>{email}</div>
      <div className={styles.note}>
        Rozpoznano operatora skrzynki — integracja przez{" "}
        <strong>{METHOD_LABEL[method]}</strong>.
      </div>
      <div className={styles.actions}>
        <Button
          className={styles.connectBtn}
          text={`Połącz przez ${METHOD_LABEL[method]}`}
          onClick={() =>
            method === "google" ? loginGoogle() : connectMicrosoft()
          }
        />
        <button className={styles.cancelBtn} onClick={onClose}>
          Anuluj
        </button>
      </div>

      {isLoading && (
        <div className={styles.pending}>Trwa konfiguracja integracji…</div>
      )}
    </div>
  );
};

export default EmailIntegrationWizard;
